import api from '@pray/shared/utils/api';

/** Where an episode's ad breaks live. */
export type AdLocationsHost = 'omny';

/** The host's default number of ads at each break; null while the host doesn't say. */
export type AdLocationsDefaultCounts = {
  pre: number | null;
  mid: number | null;
  post: number | null;
};

/** An episode's ad breaks. */
export type AdLocations = {
  host?: AdLocationsHost;
  /** The Omny clip id. */
  episode_id?: string;
  /** Ads at that break: 0 means no break, null means the host's default count, which may be unknown. */
  pre_count: number | null;
  post_count: number | null;
  /** Mid-roll locations in seconds; a timestamp repeated N times = an Nx marker (N stacked ads). */
  insertion_points: number[];
  duration_seconds: number | null;
  default_counts?: AdLocationsDefaultCounts;
  contract_version?: number;
  /** The clean source file to preview while placing markers; the published file carries ads. Not sent on a save. */
  original_audio_url?: string | null;
  /** That preview's own length, which can differ from the episode's when it came from another rendition. */
  original_audio_duration_seconds?: number | null;
};

export async function getAdLocations({ dailyItemId }: { dailyItemId: string }) {
  if (!dailyItemId) throw Error('Daily item ID is required');

  const response = await api.executeGetRequest(`/web/studio/daily-items/${dailyItemId}/ad-locations`);
  // Unwrap the API envelope ({ object, data }) down to the payload.
  return response.data?.data ?? response.data;
}

/**
 * Saves an episode's ad breaks. A null part is left as it is. `contractVersion` is the save contract the editor
 * speaks, sent only when given: the server refuses an Omny save without it.
 */
export async function updateAdLocations({
  dailyItemId,
  preCount,
  postCount,
  insertionPoints,
  contractVersion,
}: {
  dailyItemId: string;
  preCount: number | null;
  postCount: number | null;
  insertionPoints: number[] | null;
  contractVersion?: number;
}) {
  if (!dailyItemId) throw Error('Daily item ID is required');

  const response = await api.executePutRequest(`/web/studio/daily-items/${dailyItemId}/ad-locations`, {
    pre_count: preCount,
    post_count: postCount,
    insertion_points: insertionPoints,
    ...(contractVersion === undefined ? {} : { contract_version: contractVersion }),
  });
  // Unwrap the API envelope ({ object, data }) down to the payload.
  return response.data?.data ?? response.data;
}
